import { Actions, Async, F1 } from '@client/new/helpers/types';
import { useMemo } from 'react';
import {
  ChannelFilters,
  ChannelOptions,
  ChannelSort,
  ChannelSortBase,
  DefaultGenerics,
} from 'stream-chat';
import { add } from 'date-fns';
import { useInboxModel } from './useInbox.model';

export type InboxViewModelParams = {
  influencerId: number;
};

export type InboxViewModel = {
  dispatch: InboxViewDispatcher;
  isMessagingEnabled: boolean;
  submitting: boolean;
  influencerId: number;
  streamToken: string | null;
  userId: string;
  channelListOptions: {
    filters: ChannelFilters;
    options: ChannelOptions;
    sort: ChannelSortBase<DefaultGenerics>;
  };
  isBundleBusinessModel: boolean;
};

export type InboxViewActionsMap = {
  submitEnable: null;
};

export type InboxViewActions = Actions<InboxViewActionsMap>;

export type InboxViewDispatcher = F1<InboxViewActions>;

export const useInboxViewModel = (
  p: InboxViewModelParams,
): Async<InboxViewModel> => {
  const asyncModel = useInboxModel(p);

  const userId = `creator-${p.influencerId}`;
  const channelListOptions = useMemo(
    () => getChannelListOptions(userId),
    [userId],
  );

  if (asyncModel.type !== 'fetched') return asyncModel;

  const model = asyncModel.value;

  const dispatch: InboxViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        return model.dispatch(action);
    }
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      isMessagingEnabled: model.canDirectMessage,
      submitting: model.submitting,
      influencerId: p.influencerId,
      streamToken: model.streamToken,
      channelListOptions,
      userId,
      isBundleBusinessModel: model.isBundleBusinessModel,
    },
  };
};

export const getChannelListOptions = (user: string | null) => {
  const dateInTheFuture = add(new Date(), { days: 1 });
  const filters: ChannelFilters = {
    type: 'messaging',
    members: { $in: [user] },
    last_message_at: { $lt: dateInTheFuture.toISOString() },
  };

  const options: ChannelOptions = {
    state: true,
    watch: true,
    presence: true,
    limit: 100,
  };

  const sort: ChannelSort = {
    last_message_at: -1,
  };

  return {
    filters,
    options,
    sort,
  };
};
